import { useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import {

  ChevronsUpDown,
  Clock,
  Code2,
  Compass,
  Folder,
  LayoutGrid,
  LayoutTemplate,
  LogOut,
  Monitor,
  Moon,
  MoreHorizontal,
  Pencil,
  Plus,
  Settings,
  Star,
  Sun,
  Trash2,
  User,
  Users,
  Languages,
  Search,
  ShieldCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { Avatar } from '@/components/Avatar';
import { LogoMark } from '@/components/Logo';
import { Menu, type MenuEntry } from '@/components/ui/Menu';
import { ConfirmDialog, PromptDialog } from '@/components/ui/PromptDialog';
import { LOCALES, getLocale, setLocale, t, tr } from '@/i18n';
import { api, errorMessage } from '@/lib/api';
import { logout, useMe } from '@/lib/auth';
import { openCommandPalette } from '@/lib/commands';
import { workspaceName } from '@/lib/workspaceName';
import { cn } from '@/lib/cn';
import { useProjects, type Project } from '@/lib/queries';
import { useTheme } from '@/lib/theme';
import type { WorkspaceSummary } from '@/lib/types';
import { NotificationsBell } from './NotificationsBell';

function navCls({ isActive }: { isActive: boolean }) {
  return cn(
    'flex h-9 items-center gap-2.5 rounded-control px-2.5 text-[13px] font-medium transition-colors [&_svg]:size-4 [&_svg]:shrink-0',
    isActive ? 'bg-accent-soft text-accent-fg' : 'text-fg-muted hover:bg-hover hover:text-fg',
  );
}

export function WorkspaceBadge({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span
      className="inline-flex shrink-0 items-center justify-center rounded-[8px] bg-gradient-to-br from-accent-400 to-accent-700 font-semibold text-white"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      aria-hidden
    >
      {(name.trim()[0] ?? '?').toUpperCase()}
    </span>
  );
}

export function Sidebar({ workspaces, current, onSelect, onNavigate }: { workspaces: WorkspaceSummary[]; current: WorkspaceSummary; onSelect: (id: string) => void; onNavigate?: () => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { pref, setPref } = useTheme();
  const projects = useProjects(current.id);
  const [createWs, setCreateWs] = useState(false);
  const [createProject, setCreateProject] = useState(false);
  const [renameProject, setRenameProject] = useState<Project | null>(null);
  const [deleteProject, setDeleteProject] = useState<Project | null>(null);
  const canAdmin = current.role === 'owner' || current.role === 'admin';
  const canCreate = current.role !== 'guest';

  const wsItems: MenuEntry[] = [
    { group: t('dashboard.workspaces') },
    ...workspaces.map((w) => ({
      value: w.id,
      label: (
        <span className="flex items-center gap-2">
          <WorkspaceBadge name={workspaceName(w)} size={20} />
          <span className="truncate">{workspaceName(w)}</span>
        </span>
      ),
      shortcut: w.id === current.id ? '✓' : undefined,
      onSelect: () => {
        onSelect(w.id);
        navigate('/dashboard');
      },
    })),
    'separator',
    { value: 'new-ws', label: t('dashboard.newWorkspace'), icon: <Plus />, onSelect: () => setCreateWs(true) },
    { value: 'ws-settings', label: t('dashboard.workspaceSettings'), icon: <Settings />, onSelect: () => navigate(`/settings/workspace/${current.id}`) },
  ];

  const user = me.data?.user;
  const userItems: MenuEntry[] = [
    { group: user?.email ?? '' },
    { value: 'profile', label: t('dashboard.profile'), icon: <User />, onSelect: () => navigate('/settings/profile') },
    ...(user?.isAdmin ? [{ value: 'admin', label: tr("Администрирование"), icon: <ShieldCheck />, onSelect: () => navigate('/settings/admin') }] : []),
    'separator',
    { group: t('theme.label') },
    { value: 'light', label: t('theme.light'), icon: <Sun />, shortcut: pref === 'light' ? '✓' : undefined, onSelect: () => setPref('light') },
    { value: 'dark', label: t('theme.dark'), icon: <Moon />, shortcut: pref === 'dark' ? '✓' : undefined, onSelect: () => setPref('dark') },
    { value: 'system', label: t('theme.system'), icon: <Monitor />, shortcut: pref === 'system' ? '✓' : undefined, onSelect: () => setPref('system') },
    'separator',
    { group: tr("Язык") },
    ...LOCALES.map((l) => ({ value: `lang-${l.value}`, label: l.label, icon: <Languages />, shortcut: getLocale() === l.value ? '✓' : undefined, onSelect: () => setLocale(l.value) })),
    'separator',
    { value: 'logout', label: t('auth.logout'), icon: <LogOut />, onSelect: () => void logout() },
  ];

  return (
    <aside className="flex h-full w-64 shrink-0 flex-col gap-1 border-r border-border bg-surface px-3 py-3" aria-label={tr("Навигация")}>
      <div className="flex items-center gap-1">
        <Menu
          side="bottom"
          align="start"
          items={wsItems}
          trigger={
            <button type="button" className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-control px-1.5 text-left hover:bg-hover">
              <WorkspaceBadge name={workspaceName(current)} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold">{workspaceName(current)}</span>
                <span className="block truncate text-[11px] text-fg-subtle">
                  {current.personal ? t('dashboard.personal') : t('dashboard.members', { count: current.memberCount })}
                </span>
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-fg-subtle" />
            </button>
          }
        />
        <NotificationsBell />
      </div>

      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          openCommandPalette();
        }}
        className="mt-2 flex h-9 items-center gap-2.5 rounded-control bg-surface-2 px-2.5 text-[13px] text-fg-subtle transition-colors hover:text-fg [&_svg]:size-4"
      >
        <Search /> <span className="flex-1 text-left">{tr("Поиск и команды")}</span>
        <kbd className="rounded-[6px] bg-surface px-1.5 py-0.5 font-sans text-[11px] shadow-card">{/Mac|iPhone|iPad/.test(navigator.platform) ? '⌘K' : 'Ctrl K'}</kbd>
      </button>

      <nav className="mt-2 flex flex-col gap-0.5" onClick={onNavigate}>
        <NavLink to="/dashboard" end className={navCls}>
          <LayoutGrid /> {t('dashboard.allBoards')}
        </NavLink>
        <NavLink to="/dashboard/recent" className={navCls}>
          <Clock /> {t('dashboard.recent')}
        </NavLink>
        <NavLink to="/dashboard/favorites" className={navCls}>
          <Star /> {t('dashboard.favorites')}
        </NavLink>
        <NavLink to="/dashboard/shared" className={navCls}>
          <Users /> {t('dashboard.shared')}
        </NavLink>
        <NavLink to="/dashboard/templates" className={navCls}>
          <LayoutTemplate /> {t('dashboard.templates')}
        </NavLink>
        <NavLink to="/dashboard/trash" className={navCls}>
          <Trash2 /> {t('dashboard.trash')}
        </NavLink>
      </nav>

      <div className="mt-5 flex items-center justify-between px-2.5">
        <span className="text-[11px] font-semibold tracking-wide text-fg-subtle uppercase">{t('dashboard.projects')}</span>
        {canCreate && (
          <button type="button" onClick={() => setCreateProject(true)} aria-label={t('dashboard.newProject')} title={t('dashboard.newProject')} className="inline-flex size-6 items-center justify-center rounded-md text-fg-subtle hover:bg-hover hover:text-fg">
            <Plus className="size-3.5" />
          </button>
        )}
      </div>
      <nav className="flex min-h-0 flex-1 flex-col gap-0.5 overflow-y-auto" onClick={onNavigate}>
        {projects.data?.map((p) => (
          <div key={p.id} className="group relative">
            <NavLink to={`/dashboard/project/${p.id}`} className={navCls}>
              <Folder /> <span className="truncate">{p.name}</span>
            </NavLink>
            {canCreate && (
              <div className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 focus-within:opacity-100">
                <Menu
                  side="right"
                  align="start"
                  items={[
                    { value: 'rename', label: t('dashboard.renameProject'), icon: <Pencil />, onSelect: () => setRenameProject(p) },
                    ...(canAdmin ? [{ value: 'del', label: t('dashboard.deleteProject'), icon: <Trash2 />, danger: true, onSelect: () => setDeleteProject(p) }] : []),
                  ]}
                  trigger={
                    <button type="button" aria-label={t('common.more')} className="inline-flex size-8 items-center justify-center rounded-md text-fg-subtle hover:bg-hover">
                      <MoreHorizontal className="size-4" />
                    </button>
                  }
                />
              </div>
            )}
          </div>
        ))}
      </nav>

      <div className="flex flex-col gap-0.5 border-t border-border pt-2">
        <NavLink to="/explore" className={navCls}>
          <Compass /> {t('dashboard.explore')}
        </NavLink>
        <NavLink to="/developers" className={navCls}>
          <Code2 /> {t('dashboard.developers')}
        </NavLink>
        {user && (
          <Menu
            side="top"
            align="start"
            items={userItems}
            trigger={
              <button type="button" className="mt-1 flex h-11 items-center gap-2.5 rounded-control px-1.5 text-left hover:bg-hover">
                <Avatar name={user.name} src={user.avatarUrl} seed={user.id} size={30} />
                <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{user.name}</span>
              </button>
            }
          />
        )}
      </div>

      <PromptDialog
        open={createWs}
        onOpenChange={setCreateWs}
        title={t('dashboard.newWorkspace')}
        label={t('dashboard.workspaceName')}
        placeholder={tr("Команда продукта")}
        submitLabel={tr("Создать")}
        onSubmit={async (name) => {
          try {
            const res = await api<{ workspace: WorkspaceSummary }>('/api/workspaces', { body: { name } });
            await qc.invalidateQueries({ queryKey: ['me'] });
            onSelect(res.workspace.id);
            navigate('/dashboard');
          } catch (err) {
            toast.error(errorMessage(err));
            throw err;
          }
        }}
      />
      <PromptDialog
        open={createProject}
        onOpenChange={setCreateProject}
        title={t('dashboard.newProject')}
        label={t('dashboard.projectName')}
        submitLabel={tr("Создать")}
        onSubmit={async (name) => {
          const res = await api<{ project: Project }>(`/api/workspaces/${current.id}/projects`, { body: { name } });
          await qc.invalidateQueries({ queryKey: ['projects', current.id] });
          navigate(`/dashboard/project/${res.project.id}`);
        }}
      />
      <PromptDialog
        open={!!renameProject}
        onOpenChange={(v) => !v && setRenameProject(null)}
        title={t('dashboard.renameProject')}
        label={t('dashboard.projectName')}
        initial={renameProject?.name}
        onSubmit={async (name) => {
          await api(`/api/projects/${renameProject!.id}`, { method: 'PATCH', body: { name } });
          await qc.invalidateQueries({ queryKey: ['projects', current.id] });
        }}
      />
      <ConfirmDialog
        open={!!deleteProject}
        onOpenChange={(v) => !v && setDeleteProject(null)}
        title={t('dashboard.deleteProject')}
        text={tr("Доски проекта не удалятся — они останутся в воркспейсе без проекта.")}
        confirmLabel={t('common.delete')}
        onConfirm={async () => {
          await api(`/api/projects/${deleteProject!.id}`, { method: 'DELETE' });
          await qc.invalidateQueries({ queryKey: ['projects', current.id] });
          await qc.invalidateQueries({ queryKey: ['boards'] });
          navigate('/dashboard');
        }}
      />
    </aside>
  );
}
